import { type ReactNode } from "react";
import { cn } from "../../lib/utils";

// ── Theme tokens per variant ────────────────────────────────────────────

const themes = {
  gpt: {
    border: "border-[#f2efed]",
    headerBg: "bg-[#fefff0]",
    dotBg: "bg-[#eef822]",
    labelColor: "text-[#a3a800]",
  },
  cwyop: {
    border: "border-[#d5f6ef]",
    headerBg: "bg-[#eafaf7]",
    dotBg: "bg-[#26a68a]",
    labelColor: "text-ds-primary",
  },
} as const;

// ── Props ───────────────────────────────────────────────────────────────

type GptProps = {
  variant: "gpt";
  title: string;
  subtitle?: string;
};

type CwyopProps = {
  variant: "cwyop";
  icon?: ReactNode;
  title: string;
  // First author of the paper, rendered under the title — the third value a
  // clinician indexes by, beside the journal and year already in `dateLabel`.
  // Required (never defaulted) so every caller states whether it has one; null
  // where the surface genuinely has no author to show, such as a history row.
  author: string | null;
  subtitle?: string;
  quote?: string;
};

type PaperCardProps = {
  label: string;
  dateLabel: string;
  className?: string;
} & (GptProps | CwyopProps);

// ── Component ───────────────────────────────────────────────────────────

function PaperCard(props: PaperCardProps) {
  const { label, dateLabel, variant, className } = props;
  const theme = themes[variant];

  return (
    <div
      className={cn(
        "bg-white border-[1.5px] rounded-[12px] overflow-hidden flex flex-col",
        theme.border,
        className
      )}
    >
      {/* Header */}
      <div
        className={cn(
          "px-[13px] py-[4px] flex items-center justify-between rounded-t-lg",
          theme.headerBg
        )}
      >
        <div className="flex items-center gap-[5px]">
          <div className={cn("size-[5px]  rounded-[2.5px]", theme.dotBg)} />
          <span
            className={cn(
              "font-bold text-[9px] uppercase tracking-[0.4px]",
              theme.labelColor
            )}
          >
            {label}
          </span>
        </div>
        <span className="font-normal text-[9px] text-ds-slate-500">
          {dateLabel}
        </span>
      </div>

      {/* Body */}
      {variant === "gpt" ? (
        <div className="px-[13px] py-[10px] flex flex-col gap-[10px]">
          <p className="font-semibold text-[14px] leading-[1.2] tracking-[-0.042px] text-ds-black m-0 line-clamp-2">
            {props.title}
          </p>
          {props.subtitle != null && (
            <span className="font-normal text-[9px] text-ds-slate-500">
              {props.subtitle}
            </span>
          )}
        </div>
      ) : (
        <div className="px-[13px] py-[10px] flex flex-col gap-[7px]">
          {/* Article info */}
          <div className="flex gap-[6px] items-start">
            {props.icon != null && <div className="shrink-0">{props.icon}</div>}
            <div className="flex flex-col gap-[3px] min-w-0">
              <p className="font-semibold text-[14px] leading-[1.2] text-ds-primary m-0 line-clamp-2">
                {props.title}
              </p>
              {props.author !== null && (
                <span className="font-normal text-[9px] text-ds-slate-500">
                  {props.author}
                </span>
              )}
              {props.subtitle != null && (
                <span className="font-medium text-[9px] text-ds-mint-600">
                  {props.subtitle}
                </span>
              )}
            </div>
          </div>
          {/* Last question quote */}
          {props.quote != null && props.quote !== "" && (
            <div className="border-l-2 border-ds-mint-200 pl-[10px]">
              <p className="font-normal text-[13px] leading-[1.1] italic text-ds-slate-700 m-0 line-clamp-2">
                <span className="text-ds-mint-600">&ldquo;</span>
                {props.quote}
                <span className="text-ds-mint-600">&rdquo;</span>
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export { PaperCard, type PaperCardProps };
